<template>
  <section>
    <div class="container-wrap py-12">
      <h2 class="text-h1 mb-6">{{ t('title') }}</h2>
      <ul class="column-321">
        <li v-for="(item, index) in supportItems" :key="hash(item)">
          <CardDocument
            :title="t(`items.${index}.title`)"
            :description="t(`items.${index}.description`)"
            :button-name="t(`items.${index}.item-button`)"
            :button-url="item.url"
            :button-id="provideAcnTrackingID(index)"
            @click-callback="handleClick(item.trackingName)"
          />
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  interface ISupportItem {
    url: string;
    trackingName: string;
  }

  const { t, locale } = useI18n({ useScope: 'local' });
  const { isCN } = useLanguage();

  const supportItems = computed((): ISupportItem[] => [
    {
      url: `/${locale.value}/marketplace/help/sales-support`,
      trackingName: 'SalesSupport',
    },
    {
      url: `/${locale.value}/marketplace/help/technical-support`,
      trackingName: 'TechSupport',
    },
    {
      url: `/${locale.value}/marketplace/free-trial`,
      trackingName: 'FreeTrialZone',
    },
  ]);

  const handleClick = (trackingName: string): void => {
    window.gtag('event', 'site_click', { Item: trackingName });
  };

  const provideAcnTrackingID = (index: number): string | undefined => {
    if (isCN.value) {
      switch (index) {
        case 0:
          return 'tracking_ssupport';
        case 1:
          return 'tracking_tsupport';
        default:
          return undefined;
      }
    }
    return undefined;
  };
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "en-jp": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "en-tw": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "en-kr": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "en-americas": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "en-asia-pacific": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "en-emea": {
    "title": "Contact us",
    "items": {
      "0": {
        "title": "Sales Support",
        "description": "We can assist you to choose the products and solution packages or other questions related to sales.",
        "item-button": "Contact with sales support"
      },
      "1": {
        "title": "Technical Support",
        "description": "Submit your support request, let us know how we can help.",
        "item-button": "Contact with technical support"
      },
      "2": {
        "title": "Get Started Free",
        "description": "Get free hands-on experience with computing, networking, dashboard, security, enterprise applications, analytics, developer services and more.",
        "item-button": "Try our applications for free"
      }
    }
  },
  "ja-jp": {
    "title": "お問い合わせ",
    "items": {
      "0": {
        "title": "ビジネスサポート",
        "description": "適切な製品やソリューション、またはその他の関連するビジネスコンサルティングの選択をお手伝いします",
        "item-button": "ビジネスサポート"
      },
      "1": {
        "title": "技術サポート",
        "description": "サポートリクエストを作成し、私たちがどのようにあなたを助けることができるかを私たちに知らせてください",
        "item-button": "テクニカルサポートが必要"
      },
      "2": {
        "title": "製品の無料トライアルを入手",
        "description": "コンピューティング、ネットワーキング、ダッシュボード、セキュリティ、エンタープライズアプリケーション、分析、開発者サービスなどを無料で体験できます。",
        "item-button": "製品無料トライアルについて知りたい"
      }
    }
  },
  "zh-cn": {
    "title": "联系我们",
    "items": {
      "0": {
        "title": "商业支持",
        "description": "我们能帮助您筛选合适的产品与解决方案,或其他相关的商业咨询",
        "item-button": "我需要商业支持"
      },
      "1": {
        "title": "技术支持",
        "description": "提出支持需求,让我们了解如何帮助您",
        "item-button": "我需要技术支持"
      },
      "2": {
        "title": "获得产品免费试用",
        "description": "获得有关计算、网络、仪表板、安全、企业应用程序、分析、开发人员服务等方面的免费实务经验。",
        "item-button": "我想了解产品免费试用"
      }
    }
  },
  "zh-tw": {
    "title": "聯繫我們",
    "items": {
      "0": {
        "title": "商業支援",
        "description": "我們能幫助您篩選合適的產品與解決方案,或其他相關的商業諮詢",
        "item-button": "我需要商業面的支援"
      },
      "1": {
        "title": "技術支援",
        "description": "提出支援需求,讓我們瞭解如何幫助您",
        "item-button": "我需要技術面的支援"
      },
      "2": {
        "title": "取得產品免費試用",
        "description": "獲得有關計算、網絡、儀表板、安全、企業應用程式、分析、開發人員服務等方面的免費實務經驗。",
        "item-button": "我想瞭解產品免費試用"
      }
    }
  }
}
</i18n>
